import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Search, FileText, Image as ImageIcon, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp";
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);

export async function openFile(f) {
  try {
    const { data } = await api.get(`/espace/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    if (f.content_type.startsWith("image/") || f.content_type === "application/pdf") a.target = "_blank";
    else a.download = f.original_filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(formatError(e)); }
}

function UploadDialog({ open, onOpenChange, scope, categories, onDone }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(categories[0]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choisissez un fichier");
    if (file.size > 10 * 1024 * 1024) return toast.error("Fichier trop volumineux (10 Mo max)");
    const fd = new FormData();
    fd.append("scope", scope);
    fd.append("title", title || file.name);
    fd.append("category", category);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/espace/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document enregistré");
      setTitle(""); setFile(null);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ajouter un document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Emploi du temps CM2" data-testid={`${scope}-upload-title`} /></div>
          <div>
            <Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid={`${scope}-upload-category`}><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fichier (PDF, Word ou image · 10 Mo max)</Label>
            <Input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`${scope}-upload-file`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid={`${scope}-upload-submit`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {busy ? "Envoi…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({ f, canDelete, onDelete, scope }) {
  const Icon = f.content_type.startsWith("image/") ? ImageIcon : FileText;
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`${scope}-file-row`}>
      <div className="w-10 h-10 rounded-lg bg-[#E8F2EF] grid place-items-center shrink-0"><Icon className="w-5 h-5 text-[#1F7A63]" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate">{f.title}</div>
        <div className="text-xs text-[#5C736A] truncate">
          <span className="bg-[#FFF7D6] text-[#7A6100] px-1.5 py-0.5 rounded mr-1.5">{f.category}</span>
          {fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString("fr-FR")} · {f.owner_name}
        </div>
      </div>
      <Button size="icon" variant="ghost" onClick={() => openFile(f)} title="Ouvrir" data-testid={`${scope}-file-open`}><Download className="w-4 h-4" /></Button>
      {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(f)} title="Supprimer" data-testid={`${scope}-file-delete`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
    </div>
  );
}

export default function FilesPanel({ scope, canUpload, categories, emptyText }) {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/espace/files", { params: { scope, q: q || undefined } });
      setFiles(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, scope]);

  const del = async (f) => {
    if (!window.confirm(`Supprimer « ${f.title} » ?`)) return;
    try { await api.delete(`/espace/files/${f.id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="ek-card p-0" data-testid={`${scope}-files-panel`}>
      <div className="p-3 border-b border-[#E2E8F0] flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C736A]" />
          <Input className="pl-9" placeholder="Rechercher un document…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${scope}-files-search`} />
        </div>
        {canUpload && (
          <Button onClick={() => setOpen(true)} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid={`${scope}-upload-btn`}>
            <Upload className="w-4 h-4 mr-1" /> Ajouter un document
          </Button>
        )}
      </div>
      {files.map((f) => (
        <FileRow key={f.id} f={f} scope={scope} onDelete={del} canDelete={f.owner_id === user?.id || user?.role === "admin"} />
      ))}
      {files.length === 0 && <div className="text-center text-sm text-[#5C736A] py-10" data-testid={`${scope}-files-empty`}>{q ? "Aucun résultat." : emptyText}</div>}
      {canUpload && <UploadDialog open={open} onOpenChange={setOpen} scope={scope} categories={categories} onDone={load} />}
    </div>
  );
}
